/**
 * メンテナンス予告のカウントダウンの操作（折りたたみ・展開、ドラッグでの移動）と、その状態の記憶
 *
 * カウントダウンの箱（[data-maintenance-countdown]、resources/views/layouts/partials/maintenance-notice.blade.php）について:
 * - 開閉: ボタン（[data-maintenance-countdown-toggle]）を押すと、文言（[data-maintenance-countdown-text]）とドラッグのハンドル
 *   （[data-maintenance-countdown-drag]）を隠してアイコンを hourglass にする（折りたたむ）。もう一度押すと元に戻す（展開する）。
 *   アイコンと名前は、ボタンの data-expanded-icon / data-expanded-label / data-collapsed-icon / data-collapsed-label から読む
 * - 位置: 置いた場所に近いほうの画面の端（左右・上下）と、その端から箱までの距離で持つ。箱の data-anchor-x（left|right）・
 *   data-anchor-y（top|bottom）と、インラインのスタイルの CSS 変数（--float-maintenance-countdown-left・-right・-top・-bottom の
 *   うち寄せる端のもの）で当てる（resources/css/app.css）。予定のポップオーバーも同じ変数と箱を基準に置くので、一緒に動く
 * - 開閉のアニメーション: 箱の幅と高さを Web Animations API で変える。置いた側の端は動かないので、右側に置いた箱は右端に寄せて
 *   縮み左へ伸び、左側に置いた箱は左端に寄せて縮み右へ伸びる。そのあいだは中身（[data-maintenance-countdown-content]）を
 *   展開したときの幅のまま箱の右下に固定し、はみ出した分を箱で隠す（箱の幅に合わせて文言が折り返し直さないようにするため）。
 *   時間と緩急は、展開と折りたたみで別々に resources/css/app.css の CSS 変数（--float-maintenance-countdown-expand-duration・
 *   -expand-easing・-collapse-duration・-collapse-easing）で変える。動きを減らす設定のときはアニメーションしない
 * - ドラッグ: ハンドルを押したまま動かすと、箱を動かす。箱は画面の外に出さない（画面の大きさが変わったときも収め直す）
 * - 記憶: 開閉状態と位置を Cookie（maintenance_countdown）に保存する。App\View\Composers\MaintenanceNoticeComposer が読み、
 *   次のページの初期表示に使う（JS が動く前の表示が動かないようにするため。管理画面のサイドメニューと同じ方式）。
 *   値の形式は App\ViewModels\MaintenanceCountdownViewModel を参照。開始日時が違う（別のメンテナンスの）値は使われない
 */
const COOKIE_NAME = 'maintenance_countdown'
const DAY_MS = 24 * 60 * 60 * 1000
// CSS 変数が読めないときの開閉の時間と緩急
const DEFAULT_DURATION_MS = 250
const DEFAULT_EASING = 'ease-out'
// 開閉の向きごとの、時間と緩急の CSS 変数
const ANIMATION_PROPERTIES = {
    expand: {
        duration: '--float-maintenance-countdown-expand-duration',
        easing: '--float-maintenance-countdown-expand-easing',
    },
    collapse: {
        duration: '--float-maintenance-countdown-collapse-duration',
        easing: '--float-maintenance-countdown-collapse-easing',
    },
}
// 寄せる端ごとの、位置の CSS 変数
const POSITION_PROPERTIES = {
    left: '--float-maintenance-countdown-left',
    right: '--float-maintenance-countdown-right',
    top: '--float-maintenance-countdown-top',
    bottom: '--float-maintenance-countdown-bottom',
}
// Cookie の値での端の書き方
const EDGE_CODES = { left: 'l', right: 'r', top: 't', bottom: 'b' }

/**
 * 箱の CSS 変数から、開閉のアニメーションの時間と緩急を読む
 * 時間（例: 250ms、0.25s）はミリ秒にする。緩急は、形式が違うと animate() が例外を投げて箱が崩れるので、先に確かめて既定の値にする
 *
 * @param {HTMLElement} countdown
 * @param {{duration: string, easing: string}} properties ANIMATION_PROPERTIES の expand か collapse
 * @returns {{duration: number, easing: string}}
 */
const read_animation_timing = (countdown, properties) => {
    const style = getComputedStyle(countdown)
    const duration_value = style.getPropertyValue(properties.duration).trim()
    const duration_number = parseFloat(duration_value)
    let duration = DEFAULT_DURATION_MS
    if (!Number.isNaN(duration_number)) duration = duration_value.endsWith('ms') ? duration_number : duration_number * 1000

    const easing_value = style.getPropertyValue(properties.easing).trim()
    let easing = DEFAULT_EASING
    try {
        new KeyframeEffect(null, null, { easing: easing_value })
        easing = easing_value
    } catch (error) {
        // 空や形式の違う値のときは、既定の緩急で動かす
    }
    return { duration, easing }
}

/**
 * 箱を置ける範囲（スクロールバーを除いた画面の大きさ）
 *
 * @returns {{width: number, height: number}}
 */
const viewport_size = () => ({
    width: document.documentElement.clientWidth,
    height: document.documentElement.clientHeight,
})

/**
 * 箱の矩形を、指定した端（左右・上下）からの距離で表す
 *
 * @param {{left: number, top: number, right: number, bottom: number}} rect
 * @param {string} x_edge 'left' | 'right'
 * @param {string} y_edge 'top' | 'bottom'
 * @returns {{x_edge: string, x: number, y_edge: string, y: number}}
 */
const position_from_rect = (rect, x_edge, y_edge) => {
    const viewport = viewport_size()
    return {
        x_edge,
        x: x_edge === 'left' ? rect.left : viewport.width - rect.right,
        y_edge,
        y: y_edge === 'top' ? rect.top : viewport.height - rect.bottom,
    }
}

/**
 * 箱の矩形を、近いほうの端（箱の中心が画面の左半分なら左端、上半分なら上端）からの距離で表す
 *
 * @param {{left: number, top: number, right: number, bottom: number}} rect
 * @returns {{x_edge: string, x: number, y_edge: string, y: number}}
 */
const nearest_position_from_rect = (rect) => {
    const viewport = viewport_size()
    const x_edge = (rect.left + rect.right) / 2 < viewport.width / 2 ? 'left' : 'right'
    const y_edge = (rect.top + rect.bottom) / 2 < viewport.height / 2 ? 'top' : 'bottom'
    return position_from_rect(rect, x_edge, y_edge)
}

/**
 * 端からの距離を、その大きさの箱が画面の中に収まる範囲にする
 *
 * @param {{x_edge: string, x: number, y_edge: string, y: number}} position
 * @param {{width: number, height: number}} size
 * @returns {{x_edge: string, x: number, y_edge: string, y: number}}
 */
const clamp_position = (position, size) => {
    const viewport = viewport_size()
    return {
        x_edge: position.x_edge,
        x: Math.round(Math.min(Math.max(position.x, 0), Math.max(viewport.width - size.width, 0))),
        y_edge: position.y_edge,
        y: Math.round(Math.min(Math.max(position.y, 0), Math.max(viewport.height - size.height, 0))),
    }
}

/**
 * 1つのカウントダウンの箱について、開閉・ドラッグ・記憶の処理をセットアップする
 *
 * @param {HTMLElement} countdown
 * @returns {void}
 */
const setup_countdown_controls = (countdown) => {
    const content = countdown.querySelector('[data-maintenance-countdown-content]')
    const text = countdown.querySelector('[data-maintenance-countdown-text]')
    const toggle_button = countdown.querySelector('[data-maintenance-countdown-toggle]')
    const handle = countdown.querySelector('[data-maintenance-countdown-drag]')
    if (content === null || text === null || toggle_button === null) return

    // 折りたたむと隠す要素
    const expanded_parts = [text, handle].filter((element) => element !== null)
    const start_at = new Date(countdown.dataset.startAt)
    const server_now = new Date(countdown.dataset.serverNow)

    let collapsed = countdown.dataset.collapsed === 'true'
    let animation = null

    /**
     * Cookie からサーバーが描画した位置（data-anchor-x・data-anchor-y とインラインのスタイル）。無いときは null
     *
     * @returns {{x_edge: string, x: number, y_edge: string, y: number}|null}
     */
    const read_rendered_position = () => {
        const x_edge = countdown.dataset.anchorX
        const y_edge = countdown.dataset.anchorY
        if (!['left', 'right'].includes(x_edge) || !['top', 'bottom'].includes(y_edge)) return null
        const x = parseFloat(countdown.style.getPropertyValue(POSITION_PROPERTIES[x_edge]))
        const y = parseFloat(countdown.style.getPropertyValue(POSITION_PROPERTIES[y_edge]))
        return Number.isNaN(x) || Number.isNaN(y) ? null : { x_edge, x, y_edge, y }
    }

    // 利用者がドラッグで決めた位置。null のときは CSS の既定の位置（右下。resources/css/app.css）
    let preferred = read_rendered_position()

    /**
     * 箱の位置を当てる（null のときは data-anchor-* とインラインのスタイルを外して、CSS の既定の位置に戻す）
     *
     * @param {{x_edge: string, x: number, y_edge: string, y: number}|null} position
     * @returns {void}
     */
    const set_position = (position) => {
        Object.values(POSITION_PROPERTIES).forEach((property) => countdown.style.removeProperty(property))
        if (position === null) {
            countdown.dataset.anchorX = ''
            countdown.dataset.anchorY = ''
            return
        }
        countdown.dataset.anchorX = position.x_edge
        countdown.dataset.anchorY = position.y_edge
        countdown.style.setProperty(POSITION_PROPERTIES[position.x_edge], `${position.x}px`)
        countdown.style.setProperty(POSITION_PROPERTIES[position.y_edge], `${position.y}px`)
    }

    /**
     * 今の表示のまま、置いた位置による幅の制限（右に寄せた箱は画面の左端まで、左に寄せた箱は右端までの幅で縮む）を受けない
     * 箱の大きさを測る。画面の右下の角に寄せて測る（このあと apply_position で位置を当て直す。描画の前に終えるので、ちらつかない）
     *
     * @returns {{width: number, height: number}}
     */
    const measure_size = () => {
        set_position({ x_edge: 'right', x: 0, y_edge: 'bottom', y: 0 })
        const rect = countdown.getBoundingClientRect()
        return { width: rect.width, height: rect.height }
    }

    /**
     * 利用者が決めた位置を、その大きさの箱が画面に収まるようにして当てる（決めていなければ既定の位置）。当てたあとの矩形を返す
     *
     * @param {{width: number, height: number}} size
     * @returns {DOMRect}
     */
    const apply_position = (size) => {
        set_position(preferred === null ? null : clamp_position(preferred, size))
        return countdown.getBoundingClientRect()
    }

    /**
     * 箱を画面の中に収め直す（決めた位置は変えない）
     *
     * @returns {void}
     */
    const fit = () => {
        if (preferred === null || animation !== null) return
        apply_position(measure_size())
    }

    /**
     * 開閉状態と位置を Cookie に保存する。メンテナンスの開始の1日後に消える
     *
     * @returns {void}
     */
    const save_state = () => {
        const max_age = Math.max(0, Math.ceil((start_at.getTime() - server_now.getTime() + DAY_MS) / 1000))
        const position = preferred === null
            ? '-.-'
            : `${EDGE_CODES[preferred.x_edge]}${preferred.x}.${EDGE_CODES[preferred.y_edge]}${preferred.y}`
        const value = `${Math.floor(start_at.getTime() / 1000)}.${collapsed ? 1 : 0}.${position}`
        document.cookie = `${COOKIE_NAME}=${value}; path=/; max-age=${max_age}; SameSite=Lax`
    }

    /**
     * 折りたたんだ・展開した状態の表示にする（文言・ハンドルの表示と、ボタンのアイコン・名前）
     *
     * @param {boolean} is_collapsed
     * @returns {void}
     */
    const apply_state = (is_collapsed) => {
        const state = is_collapsed ? 'collapsed' : 'expanded'
        expanded_parts.forEach((element) => element.classList.toggle('hidden', is_collapsed))
        toggle_button.textContent = toggle_button.dataset[`${state}Icon`]
        toggle_button.title = toggle_button.dataset[`${state}Label`]
        toggle_button.setAttribute('aria-label', toggle_button.dataset[`${state}Label`])
        countdown.dataset.collapsed = String(is_collapsed)
    }

    /**
     * 動いているアニメーションを止め、アニメーションのために付けたスタイルを外す
     *
     * @returns {void}
     */
    const clear_animation = () => {
        if (animation !== null) animation.cancel()
        animation = null
        content.removeAttribute('style')
        countdown.style.removeProperty('overflow')
    }

    /**
     * 箱の矩形を、アニメーションのキーフレームの位置（今の置き方で寄せている端からの距離）にする
     *
     * @param {DOMRect} rect
     * @returns {Object<string, string>}
     */
    const keyframe_position = (rect) => {
        const position = position_from_rect(
            rect,
            countdown.dataset.anchorX === 'left' ? 'left' : 'right',
            countdown.dataset.anchorY === 'top' ? 'top' : 'bottom'
        )
        return { [position.x_edge]: `${position.x}px`, [position.y_edge]: `${position.y}px` }
    }

    /**
     * 折りたたむ・展開する。アニメーションの途中で押されたときは、その時点の大きさから逆向きに動かす
     *
     * @returns {void}
     */
    const toggle = () => {
        const from = countdown.getBoundingClientRect()
        clear_animation()
        collapsed = !collapsed

        // 展開したときの中身の幅と、変えたあとの箱の大きさ・位置を求める（描画の前に終えるので、ちらつかない）
        measure_size()
        apply_state(false)
        const content_width = content.getBoundingClientRect().width
        apply_state(collapsed)
        const to = apply_position(measure_size())
        save_state()
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

        // アニメーションのあいだは文言とハンドルを出したまま、中身を展開したときの幅で箱の右下に固定し、はみ出した分を隠す
        const style = getComputedStyle(countdown)
        expanded_parts.forEach((element) => element.classList.remove('hidden'))
        Object.assign(content.style, {
            position: 'absolute',
            right: style.paddingRight,
            bottom: style.paddingBottom,
            width: `${content_width}px`,
        })
        countdown.style.overflow = 'hidden'
        // 置いた側の端からの距離もキーフレームに入れる（画面に収めるために位置を変えたときは、位置も一緒に動く）。
        // 終わったあと onfinish で後片付けするまで、行き先の大きさ・位置を保つ（fill: forwards）
        animation = countdown.animate([
            { width: `${from.width}px`, height: `${from.height}px`, ...keyframe_position(from) },
            { width: `${to.width}px`, height: `${to.height}px`, ...keyframe_position(to) },
        ], { ...read_animation_timing(countdown, ANIMATION_PROPERTIES[collapsed ? 'collapse' : 'expand']), fill: 'forwards' })
        animation.onfinish = () => {
            clear_animation()
            apply_state(collapsed)
        }
    }

    /**
     * ハンドルを押したまま動かすと箱を動かし、離したときに位置を保存する
     *
     * @param {PointerEvent} event
     * @returns {void}
     */
    const start_drag = (event) => {
        if (event.button !== 0 || animation !== null) return
        // 文字の選択やフォーカスの移動を起こさない
        event.preventDefault()
        const rect = countdown.getBoundingClientRect()
        const start = { x: event.clientX, y: event.clientY, left: rect.left, top: rect.top }
        const size = { width: rect.width, height: rect.height }

        const move = (move_event) => {
            const viewport = viewport_size()
            const left = Math.min(Math.max(start.left + move_event.clientX - start.x, 0), Math.max(viewport.width - size.width, 0))
            const top = Math.min(Math.max(start.top + move_event.clientY - start.y, 0), Math.max(viewport.height - size.height, 0))
            preferred = clamp_position(nearest_position_from_rect({
                left,
                top,
                right: left + size.width,
                bottom: top + size.height,
            }), size)
            set_position(preferred)
        }
        // 離したとき（pointerup）も、取り消されたとき（pointercancel）も、キャプチャーが外れたところで終える
        const end = () => {
            handle.removeEventListener('pointermove', move)
            handle.removeEventListener('lostpointercapture', end)
            handle.style.removeProperty('cursor')
            save_state()
        }

        handle.setPointerCapture(event.pointerId)
        handle.style.cursor = 'grabbing'
        handle.addEventListener('pointermove', move)
        handle.addEventListener('lostpointercapture', end)
    }

    // Cookie からサーバーが描画した位置を、今の画面の大きさに収める
    fit()
    toggle_button.addEventListener('click', toggle)
    if (handle !== null) handle.addEventListener('pointerdown', start_drag)
    window.addEventListener('resize', fit)
}

/**
 * 初期化
 *
 * @returns {void}
 */
const init_maintenance_countdown_controls = () => {
    const countdowns = document.querySelectorAll('[data-maintenance-countdown]')
    countdowns.forEach((countdown) => setup_countdown_controls(countdown))
}

document.addEventListener('DOMContentLoaded', init_maintenance_countdown_controls)
